import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Container } from "../ui";
import { cn } from "../../utils/cn";
import {
  fetchPublicOffers,
  PublicOfferItem,
} from "../../apis/offers";
import {
  fetchPopularShops,
  PopularShopItem,
} from "../../apis/shops";

export interface LiveProductsProps {
  className?: string;
}

const DEFAULT_OFFER_IMG = "/deals/deal_salon_cosmetics.jpg";
const DEFAULT_SHOP_IMG = "/deals/shop_glow_salon.jpg";

const LiveProducts: React.FC<LiveProductsProps> = ({ className }) => {
  const [offers, setOffers] = useState<PublicOfferItem[]>([]);
  const [popularShops, setPopularShops] = useState<PopularShopItem[]>([]);
  const [loadingOffers, setLoadingOffers] = useState<boolean>(true);
  const [loadingShops, setLoadingShops] = useState<boolean>(true);
  const [isLocationBased, setIsLocationBased] = useState<boolean>(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(false);

  // Load offers with or without location
  const loadOffers = useCallback(async (lat?: number | null, lng?: number | null) => {
    setLoadingOffers(true);
    try {
      const res = await fetchPublicOffers({ lat, lng, limit: 4 });
      setOffers(res.offers || []);
      setIsLocationBased(res.isLocationBased || false);
    } catch (e) {
      console.warn("Failed to load offers:", e);
      setOffers([]);
    } finally {
      setLoadingOffers(false);
    }
  }, []);

  // Request browser geolocation
  const requestLocation = useCallback(() => {
    if (typeof window === "undefined" || !("geolocation" in navigator)) {
      loadOffers();
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setIsLoadingLocation(false);
        loadOffers(pos.coords.latitude, pos.coords.longitude);
      },
      (_err) => {
        // Location denied, timed out, or unavailable -> show popular offers
        setIsLoadingLocation(false);
        loadOffers(null, null);
      },
      { timeout: 4000, maximumAge: 300000 }
    );
  }, [loadOffers]);

  // Initial load
  useEffect(() => {
    let mounted = true;

    // Load popular shops from backend
    setLoadingShops(true);
    fetchPopularShops(4)
      .then((shops) => {
        if (mounted) {
          setPopularShops(shops || []);
        }
      })
      .catch((err) => {
        console.warn("Failed to fetch popular shops:", err);
        if (mounted) setPopularShops([]);
      })
      .finally(() => {
        if (mounted) setLoadingShops(false);
      });

    // Check location permission state if supported, else request location
    if (typeof window !== "undefined" && "permissions" in navigator) {
      navigator.permissions
        .query({ name: "geolocation" as PermissionName })
        .then((perm) => {
          if (!mounted) return;
          if (perm.state === "granted") {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                if (mounted) loadOffers(pos.coords.latitude, pos.coords.longitude);
              },
              () => {
                if (mounted) loadOffers();
              }
            );
          } else if (perm.state === "prompt") {
            // Automatically prompt with timeout
            requestLocation();
          } else {
            // Denied: directly show popular offers
            loadOffers(null, null);
          }
        })
        .catch(() => {
          if (mounted) requestLocation();
        });
    } else {
      requestLocation();
    }

    return () => {
      mounted = false;
    };
  }, [loadOffers, requestLocation]);

  return (
    <section
      id="deals"
      className={cn(
        "py-16 sm:py-20 lg:py-24 bg-[#FAF9FD] relative overflow-hidden",
        className
      )}
    >
      <div className="absolute top-10 right-[-120px] w-[480px] h-[480px] bg-purple-300/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-[-80px] left-[-100px] w-[420px] h-[420px] bg-emerald-200/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <Container size="xl" padding="md" className="relative z-10">
        {/* ================= Part 1: Offers (Near You vs. Popular Offers) ================= */}
        <div className="mb-14 sm:mb-16">
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 sm:mb-10 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="inline-block text-[11px] font-black uppercase tracking-wider text-[#059669]">
                  FEATURED OFFERS
                </span>
                {isLocationBased ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-purple-700 bg-purple-100/80 px-2 py-0.5 rounded-full">
                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    Nearby
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={requestLocation}
                    disabled={isLoadingLocation}
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-600 hover:text-purple-700 bg-slate-200/70 hover:bg-purple-100 px-2 py-0.5 rounded-full transition-colors cursor-pointer"
                    title="Find offers near your current location"
                  >
                    <svg
                      className={cn("w-2.5 h-2.5", isLoadingLocation && "animate-spin")}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {isLoadingLocation ? "Detecting..." : "Use location"}
                  </button>
                )}
              </div>

              {/* Dynamic Title based on Location */}
              <h2 className="text-2xl sm:text-3xl lg:text-[34px] font-extrabold text-[#0F172A] tracking-tight">
                {isLocationBased ? "Offers near you" : "Popular offers"}
              </h2>
            </div>

            <Link
              to="/offers"
              className="glass-light self-start sm:self-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold text-slate-700 transition-all hover:-translate-y-0.5"
            >
              See all <span className="text-purple-600 font-bold">&rarr;</span>
            </Link>
          </div>

          {/* 4 Offers Grid / Loading / Empty */}
          {loadingOffers ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="rounded-2xl sm:rounded-3xl bg-white border border-slate-100 h-72 animate-pulse" />
              ))}
            </div>
          ) : offers.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-slate-200 shadow-xs p-6">
              <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center text-xl mx-auto mb-3">
                🏷️
              </div>
              <p className="text-slate-600 text-sm font-bold">No active offers available right now.</p>
              <p className="text-slate-400 text-xs mt-1">Check back soon for new discounts and promotions.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {offers.map((offer, index) => (
                <motion.div
                  key={offer.id}
                  className="glass-light rounded-2xl sm:rounded-3xl hover:shadow-[0_24px_48px_-18px_rgba(76,29,149,0.32)] transition-shadow duration-300 flex flex-col justify-between overflow-hidden group"
                  whileHover={{ y: -4 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.08 }}
                >
                  {/* Image & Discount Badge */}
                  <div className="relative h-44 sm:h-48 overflow-hidden bg-slate-100">
                    <img
                      src={offer.image || DEFAULT_OFFER_IMG}
                      alt={offer.title}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src = DEFAULT_OFFER_IMG;
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />

                    {/* Green Discount Pill Badge */}
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/25 to-transparent pointer-events-none" aria-hidden="true" />
                    <div className="absolute top-3 left-3 px-2.5 py-1 bg-[#10B981]/90 backdrop-blur-md text-white text-[11px] font-extrabold rounded-md border border-white/30 shadow-[0_6px_14px_-4px_rgba(16,185,129,0.6)]">
                      {offer.badge}
                    </div>
                  </div>

                  {/* Offer Details */}
                  <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-base sm:text-[17px] font-extrabold text-[#0F172A] mb-1.5 line-clamp-1 group-hover:text-purple-700 transition-colors">
                        {offer.title}
                      </h3>
                      <div className="flex items-center text-xs text-slate-500 font-medium gap-1.5 mb-4">
                        <span className="flex items-center gap-1 truncate max-w-[150px]">
                          <svg
                            className="w-3.5 h-3.5 text-slate-400 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                          <span className="truncate">{offer.merchant}</span>
                        </span>
                        <span>&bull;</span>
                        <span className="flex-shrink-0 font-medium text-purple-700">
                          {offer.distance || offer.category || "Popular"}
                        </span>
                      </div>
                    </div>

                    <Link
                      to={offer.href}
                      className="inline-flex items-center text-xs sm:text-sm font-bold text-purple-700 hover:text-purple-900 group/link transition-colors pt-2 border-t border-slate-100"
                    >
                      View deal
                      <span className="ml-1 transition-transform group-hover/link:translate-x-1">
                        &rarr;
                      </span>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* ================= Part 2: Popular Shops ================= */}
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-extrabold text-[#0F172A] tracking-tight">
              Popular shops
            </h2>

            <Link
              to="/shops"
              className="inline-flex items-center text-xs sm:text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              See all shops <span className="ml-1">&rarr;</span>
            </Link>
          </div>

          {/* 4 Popular Shop Cards / Loading / Empty */}
          {loadingShops ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 rounded-2xl bg-white border border-slate-100 animate-pulse" />
              ))}
            </div>
          ) : popularShops.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-2xl border border-dashed border-slate-200">
              <p className="text-slate-500 text-sm font-medium">No partner shops available right now.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {popularShops.map((shop, index) => (
                <motion.div
                  key={shop.id || shop.name}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                >
                  <Link
                    to={shop.slug ? `/shops/${shop.slug}` : "/shops"}
                    className="glass-light flex items-center gap-3.5 p-3 rounded-2xl hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-16px_rgba(76,29,149,0.3)] transition-all duration-300 group"
                  >
                    {/* Shop Thumbnail */}
                    <div className="w-12 h-12 rounded-xl bg-purple-50 overflow-hidden flex-shrink-0 border border-slate-100">
                      <img
                        src={shop.image || DEFAULT_SHOP_IMG}
                        alt={shop.name}
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src = DEFAULT_SHOP_IMG;
                        }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>

                    {/* Shop Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-[#0F172A] truncate group-hover:text-purple-700 transition-colors">
                        {shop.name}
                      </h4>
                      <p className="text-[11px] text-slate-500 truncate mb-0.5">
                        {shop.category}
                      </p>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                        <span className="text-amber-500 flex items-center gap-0.5">
                          ★ {shop.rating}
                        </span>
                        <span className="text-slate-300">&bull;</span>
                        <span className="text-emerald-600 font-bold">
                          {shop.offersCount}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export { LiveProducts };
export default LiveProducts;
